import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import { useSiteContent } from '@/contexts/SiteContentContext';
import EditContentPopup from '@/components/EditContentPopup';

/**
 * Inline-editable text (admin Edit Mode only). Renders the override if set, else the default children.
 * Usage: <EditableText id="home.hero.title" as="h1" className="...">Default Heading</EditableText>
 */
export default function EditableText({ id, as: Tag = 'span', className = '', children }) {
  const { content, editMode, isAdmin, updateContent, resetContent } = useSiteContent();
  const [editing, setEditing] = useState(false);

  const defaultText = typeof children === 'string'
    ? children
    : (Array.isArray(children) ? children.filter(c => typeof c === 'string').join('') : '');
  const stored = content[id];
  const current = (stored !== undefined && stored !== null && typeof stored === 'string') ? stored : defaultText;

  if (!isAdmin || !editMode) {
    return <Tag className={className} style={{ whiteSpace: 'pre-line' }}>{current}</Tag>;
  }

  return (
    <Tag className={`${className} relative`} style={{ outline: '1px dashed rgba(181,129,59,0.7)', outlineOffset: '3px', whiteSpace: 'pre-line' }}>
      {current}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(true); }}
        className="absolute -top-3 -right-3 z-40 bg-[#B5813B] text-white rounded-full p-1 shadow-lg hover:bg-[#9c6f31]"
        title="Edit text"
        data-testid={`edit-text-${id}`}
      >
        <Pencil className="w-3 h-3" />
      </button>
      {editing && (
        <EditContentPopup
          title="Edit text"
          fields={[{ name: 'value', label: 'Text', type: 'textarea', value: current }]}
          onSave={async (vals) => { await updateContent(id, vals.value); setEditing(false); }}
          onReset={async () => { await resetContent(id); setEditing(false); }}
          onClose={() => setEditing(false)}
        />
      )}
    </Tag>
  );
}
